/**
 * Copy and contract for the USA waiting-list page (`/us-waitinglist`).
 *
 * Everything the page says lives here so the copy spec can hold it to the
 * house rules (no em or en dashes) and so the consent sentence the visitor
 * sees is, byte for byte, the sentence stored with their row. English only:
 * every visitor reaches the page from a QR card handed out in New York.
 */

export const US_WAITLIST_PATH = "/us-waitinglist";
export const US_WAITLIST_API_PATH = "/api/us-waitinglist";

/** Query parameter the QR card carries so event signups can be told apart. */
export const US_WAITLIST_SOURCE_PARAM = "src";
export const US_WAITLIST_DEFAULT_SOURCE = "direct";

/**
 * WHO-6511: who the page is for. Absent is the QR-card page for buyers; `seller` is where every
 * sell button on `/en-us` leads while selling from the US is closed (`useSellRoute`).
 */
export const US_WAITLIST_AUDIENCE_PARAM = "for";
export const US_WAITLIST_SELLER_AUDIENCE = "seller";

export type UsWaitlistAudience = "buyer" | "seller";

export const toWaitlistAudience = (value: unknown): UsWaitlistAudience =>
  value === US_WAITLIST_SELLER_AUDIENCE ? "seller" : "buyer";

/**
 * A seller signup's source always starts with this, then the button it came from
 * (`sell_header`), so the register tells seller signups apart from the QR cards'.
 */
export const US_WAITLIST_SELLER_SOURCE_PREFIX = "sell";

/** Bump when the wording of the consent sentence or purpose statement changes. */
export const US_WAITLIST_CONSENT_VERSION = "2026-09-us-waitlist-v1";
/**
 * The seller variant's consent sentence and its version. Kept in code beside the buyer's for the
 * same reason: the sentence a visitor ticks is stored byte for byte with their row, so its
 * wording is versioned here rather than editable in Lokalise. The rest of the seller copy is in
 * Lokalise (`sellers-landing:us-waitlist-seller-*`).
 */
export const US_WAITLIST_SELLER_CONSENT_VERSION =
  "2026-11-us-seller-waitlist-v1";
export const US_WAITLIST_SELLER_CONSENT =
  "Yes, email me a confirmation and let me know when I can sell on Whoppah from the US.";

/** Bump when the page around the form changes materially. */
export const US_WAITLIST_PAGE_VERSION = "2026-09-11b";

/** Which register the backend files the row under. */
export const US_WAITLIST_LIST = "us_launch";

export const US_WAITLIST_ROLES = [
  { value: "interior_designer", label: "Interior designer" },
  { value: "architect", label: "Architect" },
  { value: "home_stager", label: "Home stager" },
  { value: "showroom_or_dealer", label: "Showroom or dealer" },
  { value: "buying_for_myself", label: "Buying for myself" },
  { value: "other", label: "Other" },
] as const;

export type UsWaitlistRole = (typeof US_WAITLIST_ROLES)[number]["value"];

export const US_WAITLIST_COPY = {
  title: "Whoppah is coming to New York",
  metaDescription:
    "Join the waiting list. Europe's marketplace for vintage design is opening to New York.",
  ogDescription:
    "Europe's marketplace for vintage design. 65,000 pieces, no two alike.",
  headline: "Whoppah is coming to New York.",
  intro:
    "Europe's marketplace for vintage design. Around 65,000 pieces, including Vitra, Knoll and Herman Miller. We're opening to New York, and to the designers who source for their clients.",
  benefitsTitle: "Free of charge account when we open in the US for:",
  benefits: ["Pro Buyers", "Merchant Sellers", "Consumer Buyers & Sellers"],
  nameLabel: "Name",
  emailLabel: "Email",
  companyLabel: "Company or studio",
  roleLabel: "I am a…",
  rolePlaceholder: "Select",
  phoneLabel: "Phone",
  optional: "(optional)",
  purpose:
    "We'll use your name and email to tell you when Whoppah opens in the United States, and to invite you to our professional programme for designers. We won't sell your details or pass them to anyone else. You can unsubscribe at any time, and you can ask us to delete your details by mailing",
  privacyContact: "support@whoppah.com",
  privacyPolicyLabel: "Privacy policy",
  privacyPolicyPath: "/privacy-policy",
  consent:
    "Yes, email me a confirmation and let me know when Whoppah launches in the US.",
  submit: "Join the waiting list",
  submitting: "One moment…",
  validationError: "Please fill in your name, email, and tick the box above.",
  submitError:
    "Something went wrong on our side. Please try again, or email us at support@whoppah.com.",
  doneTitle: "You're on the list.",
  doneBody:
    "We'll email you when we open in New York. Your account will be free of charge.",
  doneReply:
    "One thing that would help: reply to the confirmation email and tell us what you're looking for. We read every one, and it shapes what we bring over.",
  footerSite: "whoppah.com",
  footerMail: "support@whoppah.com",
} as const;
